import { createClient } from '@/lib/supabase/client';
import { readClubPausedBody, type ClubPausedBody } from '@/app/[locale]/club/libs/clubPaused';

export type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  // If provided, will be JSON.stringified and sent as application/json
  body?: unknown;
  signal?: AbortSignal;
  /** Pin private requests to the account that initiated them. */
  expectedUserId?: string;
};

export type ApiError = Error & {
  status?: number;
  code?: string;
  details?: string;
  hint?: string;
  /** Set on a 403 `club_paused` refusal (Members phase 5): the module, reason and until. */
  clubPaused?: ClubPausedBody;
};

function normalizePath(path: string): string {
  if (!path) return '/api';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // Enforce /api prefix per project rule
  if (!path.startsWith('/api/')) return `/api${path.startsWith('/') ? '' : '/'}${path}`;
  return path;
}

async function readResponse<TResponse>(res: Response): Promise<TResponse> {
  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!res.ok) {
    let payload: unknown = undefined;
    try {
      payload = isJson ? await res.json() : await res.text();
    } catch {
      // ignore parse error
    }
    const message = (() => {
      if (payload && typeof payload === 'object') {
        const maybe = payload as { error?: unknown; message?: unknown };
        if (typeof maybe.error === 'string') return maybe.error;
        if (typeof maybe.message === 'string') return maybe.message;
      }
      if (typeof payload === 'string' && payload.trim()) return payload;
      return `Request failed with ${res.status}`;
    })();
    const err: ApiError = new Error(message);
    err.status = res.status;
    if (payload && typeof payload === 'object') {
      const meta = payload as { code?: unknown; details?: unknown; hint?: unknown };
      if (typeof meta.code === 'string') err.code = meta.code;
      if (typeof meta.details === 'string') err.details = meta.details;
      if (typeof meta.hint === 'string') err.hint = meta.hint;
    }
    const paused = res.status === 403 ? readClubPausedBody(payload) : null;
    if (paused) err.clubPaused = paused;
    throw err;
  }

  if (res.status === 204) return undefined as unknown as TResponse;
  return (isJson ? await res.json() : ((await res.text()) as unknown)) as TResponse;
}

export async function apiRequest<TResponse = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  const url = normalizePath(path);
  const supabase = createClient();
  let accessToken: string | undefined;
  let sessionUserId: string | undefined;
  try {
    const sessionData = await supabase?.auth.getSession();
    accessToken = sessionData?.data.session?.access_token || undefined;
    sessionUserId = sessionData?.data.session?.user?.id;
  } catch {
    // ignore and proceed without token
  }

  if (options.expectedUserId && sessionUserId !== options.expectedUserId)
    throw new Error('Account changed');

  const doFetch = async (token: string | undefined): Promise<Response> => {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(options.headers || {}),
    };

    if (token) headers['Authorization'] = `Bearer ${token}`;

    const init: RequestInit = {
      method: options.method || (options.body !== undefined ? 'POST' : 'GET'),
      headers,
      signal: options.signal,
    };

    if (options.body !== undefined) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      init.body =
        headers['Content-Type'] === 'application/json'
          ? JSON.stringify(options.body)
          : (options.body as BodyInit);
    }

    return fetch(url, init);
  };

  let res = await doFetch(accessToken);

  // A long-open or backgrounded tab can hold an expired access token: the client's
  // auto-refresh timer is throttled/stopped while the tab is hidden, so getSession()
  // above may hand back a stale token. The server is stateless (bearer-only — it cannot
  // refresh) and 401s. Refresh the session once and retry with the fresh token so an
  // active user never sees a spurious Unauthorized.
  if (res.status === 401) {
    let refreshedToken: string | undefined;
    try {
      const refreshed = await supabase?.auth.refreshSession();
      refreshedToken =
        !options.expectedUserId || refreshed?.data.session?.user?.id === options.expectedUserId
          ? refreshed?.data.session?.access_token || undefined
          : undefined;
    } catch {
      // refresh failed (no/expired refresh token) — keep the original 401 below
    }
    if (refreshedToken && refreshedToken !== accessToken) {
      res = await doFetch(refreshedToken);
    }
  }
  return readResponse<TResponse>(res);
}

export async function apiGet<TResponse = unknown>(
  path: string,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {},
) {
  return apiRequest<TResponse>(path, { ...options, method: 'GET' });
}

/**
 * GET for public, session-independent endpoints. Sends no Authorization header
 * (Vercel's CDN does not cache requests that carry one), so a signed-in viewer
 * shares the same cached response as everyone else. Cookies are sent same-origin:
 * they do not affect caching, and dropping them would lose Vercel's `_vercel_jwt`
 * on protected preview deployments. Same /api base path and error semantics as
 * `apiGet`.
 */
export async function publicGet<TResponse = unknown>(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<TResponse> {
  const res = await fetch(normalizePath(path), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    signal: options.signal,
  });
  return readResponse<TResponse>(res);
}

export async function apiPost<TResponse = unknown>(
  path: string,
  body?: unknown,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {},
) {
  return apiRequest<TResponse>(path, { ...options, method: 'POST', body });
}

export async function apiPatch<TResponse = unknown>(
  path: string,
  body?: unknown,
  options: Omit<ApiRequestOptions, 'method' | 'body'> = {},
) {
  return apiRequest<TResponse>(path, { ...options, method: 'PATCH', body });
}

export async function apiDelete<TResponse = unknown>(
  path: string,
  options: Omit<ApiRequestOptions, 'method'> = {},
) {
  return apiRequest<TResponse>(path, { ...options, method: 'DELETE' });
}

export async function apiFileUpload<TResponse = unknown>(
  path: string,
  formData: FormData,
): Promise<TResponse> {
  const url = normalizePath(path);
  const supabase = createClient();
  let accessToken: string | undefined;
  try {
    const sessionData = await supabase?.auth.getSession();
    accessToken = sessionData?.data.session?.access_token || undefined;
  } catch {
    // ignore and proceed without token
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData: {
      message?: string;
      error?: string;
      code?: string;
      details?: string;
      hint?: string;
    };
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }

    const error: ApiError = new Error(
      errorData.message || errorData.error || `HTTP ${response.status}`,
    );
    error.status = response.status;
    error.code = errorData.code;
    error.details = errorData.details;
    error.hint = errorData.hint;
    throw error;
  }

  return response.json();
}
